import { useState, useEffect } from "react";
import { Plus, Trash2, Edit2, Users, ShieldCheck, X, Search, RefreshCw } from "lucide-react";

import { roles as rolesApi, permissions as permissionsApi } from "../lib/api.js";
import { useApi } from "../hooks/index.js";
import { Spinner, Empty, CloseX } from "../components/ui.jsx";
import { useLang } from "../lib/i18n.jsx";

// ─── ROLE FORM MODAL (create / edit) ─────────────────────────────────────────

function RoleModal({ role, groupedPermissions, onClose, onSaved, toast }) {
  const { t } = useLang();
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [permCodes, setPermCodes] = useState(role?.permissions ?? []);
  const [saving, setSaving] = useState(false);

  const togglePerm = (code) => setPermCodes(p => p.includes(code) ? p.filter(x => x !== code) : [...p, code]);
  const toggleCategory = (perms) => {
    const codes = perms.map(p => p.code);
    const allSelected = codes.every(c => permCodes.includes(c));
    setPermCodes(p => allSelected ? p.filter(c => !codes.includes(c)) : [...new Set([...p, ...codes])]);
  };

  const save = async () => {
    if (!name) { toast(t("roles.nameRequired")); return; }
    setSaving(true);
    try {
      if (role) {
        await rolesApi.update(role.id, { name, description });
        await rolesApi.updatePermissions(role.id, permCodes);
      } else {
        await rolesApi.create({ name, description, permissionCodes: permCodes });
      }
      toast(role ? t("roles.roleUpdated") : t("roles.roleCreated"));
      onSaved();
      onClose();
    } catch (e) {
      toast(e.message ?? t("roles.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-ttl">{role ? t("roles.editRole") : t("roles.newRole")} <CloseX onClick={onClose} /></div>

        <div className="f-grp">
          <label className="f-lbl">{t("roles.name")}</label>
          <input className="f-in" value={name} onChange={e => setName(e.target.value)} disabled={role?.isSystem} />
        </div>
        <div className="f-grp">
          <label className="f-lbl">{t("roles.description")}</label>
          <input className="f-in" value={description} onChange={e => setDescription(e.target.value)} />
        </div>

        <div className="f-grp">
          <label className="f-lbl">{t("roles.permissions")}</label>
          <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8, padding: 10 }}>
            {Object.entries(groupedPermissions).map(([category, perms]) => {
              const codes = perms.map(p => p.code);
              const allSelected = codes.every(c => permCodes.includes(c));
              return (
                <div key={category} style={{ marginBottom: 8 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 4, cursor: "pointer" }}>
                    <input type="checkbox" checked={allSelected} onChange={() => toggleCategory(perms)} />
                    {category}
                  </label>
                  {perms.map(p => (
                    <label key={p.code} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer", padding: "2px 0 2px 22px" }}>
                      <input data-testid="permission-checkbox" type="checkbox" checked={permCodes.includes(p.code)} onChange={() => togglePerm(p.code)} />
                      {p.code}
                      {p.description && <span style={{ color: "var(--ghost)", fontSize: 11 }}> — {p.description}</span>}
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-out" onClick={onClose}>{t("common.cancel")}</button>
          <button className="btn btn-teal" style={{ flex: 1 }} onClick={save} disabled={saving || !name}>
            {saving ? t("common.sending") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── USERS MODAL ──────────────────────────────────────────────────────────────

function RoleUsersModal({ role, onClose }) {
  const { t } = useLang();
  const { data, loading } = useApi(() => rolesApi.getUsers(role.id), [role.id]);
  const users = data?.users ?? [];

  return (
    <div className="modal-ov open">
      <div className="modal">
        <div className="modal-ttl">{t("roles.usersInRole", { name: role.name })} <CloseX onClick={onClose} /></div>
        {loading ? <Spinner /> : users.length === 0
          ? <Empty icon="👤" message={t("roles.noUsersAssigned")} />
          : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {users.map(u => (
                <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: "var(--ghost)" }}>{u.email}</div>
                  </div>
                  <span className={`badge ${u.isActive ? "bg-teal" : "bg-gray"}`}>{u.isActive ? t("supportTeam.active") : t("supportTeam.inactive")}</span>
                </div>
              ))}
            </div>
          )}
        <div style={{ marginTop: 14 }}>
          <button className="btn btn-out" style={{ width: "100%" }} onClick={onClose}>{t("common.close")}</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

// ─── ROLES TAB ────────────────────────────────────────────────────────────────

function RolesTab({ toast, rolesList, loading, reload, groupedPermissions, search: externalSearch }) {
  const { t } = useLang();
  const [editRole, setEditRole] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [usersRole, setUsersRole] = useState(null);
  const [search, setSearch] = useState(externalSearch ?? "");
  const [status, setStatus] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");

  useEffect(() => { if (externalSearch !== undefined) setSearch(externalSearch); }, [externalSearch]);

  const remove = async (role) => {
    if (!window.confirm(t("roles.confirmDelete", { name: role.name }))) return;
    try {
      await rolesApi.delete(role.id);
      toast(t("roles.roleDeleted"));
      reload();
    } catch (e) {
      toast(e.message ?? t("roles.deleteFailed"));
    }
  };

  const filtered = rolesList.filter(r => {
    if (search && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (status === "active" && !r.isActive) return false;
    if (status === "inactive" && r.isActive) return false;
    if (createdFrom && new Date(r.createdAt) < new Date(createdFrom)) return false;
    return true;
  });

  const reset = () => { setSearch(""); setStatus(""); setCreatedFrom(""); };
  const hasFilters = search || status || createdFrom;

  return (
    <div>
      <div className="sec-hd">
        <div>
          <div className="sec-ttl">{t("roles.title")}</div>
          <div className="sec-sub">{t("roles.found", { n: filtered.length })}</div>
        </div>
        <button data-testid="create-role-button" className="btn btn-teal" onClick={() => setCreateOpen(true)}>
          <Plus size={12} /> {t("roles.newRole")}
        </button>
      </div>

      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 320, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input
            placeholder={t("roles.searchPlaceholder")}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)", display: "flex", alignItems: "center" }}>
              <X size={12} />
            </button>
          )}
        </div>
        <select className="f-in" style={{ width: "auto" }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">{t("roles.statusAll")}</option>
          <option value="active">{t("roles.statusActive")}</option>
          <option value="inactive">{t("roles.statusInactive")}</option>
        </select>
        <input className="f-in" style={{ width: "auto" }} type="date" title={t("roles.createdFrom")} value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} />
        {hasFilters && (
          <button className="btn btn-out btn-sm" onClick={reset}><RefreshCw size={12} /> {t("users.clear")}</button>
        )}
      </div>

      <div className="card">
        {loading ? <Spinner /> : filtered.length === 0
          ? <div style={{ padding: 24 }}><Empty icon="🛡" message={hasFilters ? t("roles.noResultsFiltered") : t("roles.noneFound")} /></div>
          : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("roles.name")}</th><th>{t("roles.description")}</th>
                    <th>{t("roles.permissionCount")}</th><th>{t("roles.userCount")}</th>
                    <th>{t("tbl.status")}</th><th>{t("tbl.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr data-testid="roles-row" key={r.id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 700 }}>{r.name}</span>
                          {r.isSystem && <span className="badge bg-blue" style={{ fontSize: 9 }}>{t("roles.system")}</span>}
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: "var(--muted)" }}>{r.description ?? "—"}</td>
                      <td style={{ fontSize: 12 }}>
                        <span className="badge bg-gray">{r.permissions.length === 23 ? t("roles.all") : r.permissions.length}</span>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        <button className="btn btn-out btn-xs" onClick={() => setUsersRole(r)}>
                          <Users size={10} /> {r.userCount}
                        </button>
                      </td>
                      <td>
                        <span className={`badge ${r.isActive ? "bg-teal" : "bg-gray"}`}>
                          {r.isActive ? t("roles.statusActive") : t("roles.statusInactive")}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button className="btn btn-out btn-xs" onClick={() => setEditRole(r)}>
                            <Edit2 size={10} /> {t("common.edit")}
                          </button>
                          <button className="btn btn-out btn-xs" disabled={r.isSystem || r.userCount > 0} onClick={() => remove(r)}
                            title={r.isSystem ? t("roles.cannotDeleteSystem") : r.userCount > 0 ? t("roles.cannotDeleteAssigned") : ""}>
                            <Trash2 size={10} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {createOpen && (
        <RoleModal groupedPermissions={groupedPermissions} onClose={() => setCreateOpen(false)} onSaved={reload} toast={toast} />
      )}
      {editRole && (
        <RoleModal role={editRole} groupedPermissions={groupedPermissions} onClose={() => setEditRole(null)} onSaved={reload} toast={toast} />
      )}
      {usersRole && (
        <RoleUsersModal role={usersRole} onClose={() => setUsersRole(null)} />
      )}
    </div>
  );
}

// ─── PERMISSIONS TAB ──────────────────────────────────────────────────────────

function PermissionsTab({ permsList, groupedPermissions, search: externalSearch }) {
  const { t } = useLang();
  const [search, setSearch] = useState(externalSearch ?? "");
  const [category, setCategory] = useState("");

  useEffect(() => { if (externalSearch !== undefined) setSearch(externalSearch); }, [externalSearch]);

  const categories = Object.keys(groupedPermissions);

  const filtered = permsList.filter(p => {
    if (search) {
      const q = search.toLowerCase();
      if (!p.code.toLowerCase().includes(q) && !(p.description ?? "").toLowerCase().includes(q)) return false;
    }
    if (category && p.category !== category) return false;
    return true;
  });

  const reset = () => { setSearch(""); setCategory(""); };
  const hasFilters = search || category;

  return (
    <div>
      <div className="sec-hd">
        <div>
          <div className="sec-ttl">{t("permissions.title")}</div>
          <div className="sec-sub">{t("permissions.found", { n: filtered.length })}</div>
        </div>
      </div>

      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 320, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input
            placeholder={t("permissions.searchPlaceholder")}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)", display: "flex", alignItems: "center" }}>
              <X size={12} />
            </button>
          )}
        </div>
        <select className="f-in" style={{ width: "auto" }} value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">{t("permissions.allCategories")}</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {hasFilters && (
          <button className="btn btn-out btn-sm" onClick={reset}><RefreshCw size={12} /> {t("users.clear")}</button>
        )}
      </div>

      <div className="card">
        {filtered.length === 0
          ? <div style={{ padding: 24 }}><Empty icon="🔑" message={t("permissions.noneFound")} /></div>
          : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("permissions.code")}</th><th>{t("permissions.description")}</th><th>{t("permissions.category")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.code}>
                      <td style={{ fontSize: 12, fontFamily: "var(--fm)", fontWeight: 700 }}>{p.code}</td>
                      <td style={{ fontSize: 12, color: "var(--muted)" }}>{p.description ?? "—"}</td>
                      <td><span className="badge bg-blue" style={{ fontSize: 9 }}>{p.category ?? "Other"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function PageRoles({ toast, search }) {
  const { t } = useLang();
  const { data, loading, reload } = useApi(() => rolesApi.list(), []);
  const { data: permsData } = useApi(() => permissionsApi.list(), []);
  const [tab, setTab] = useState("roles");

  const rolesList = data?.roles ?? [];
  const groupedPermissions = permsData?.grouped ?? {};
  const permsList = permsData?.permissions ?? [];

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button className={`btn btn-sm ${tab === "roles" ? "btn-teal" : "btn-out"}`} onClick={() => setTab("roles")}>
          <ShieldCheck size={12} /> {t("roles.tabRoles")}
        </button>
        <button className={`btn btn-sm ${tab === "permissions" ? "btn-teal" : "btn-out"}`} onClick={() => setTab("permissions")}>
          <Users size={12} /> {t("roles.tabPermissions")}
        </button>
      </div>

      {tab === "roles"
        ? <RolesTab toast={toast} rolesList={rolesList} loading={loading} reload={reload} groupedPermissions={groupedPermissions} search={search} />
        : <PermissionsTab permsList={permsList} groupedPermissions={groupedPermissions} search={search} />}
    </div>
  );
}
